/**
 * Asking for the terms NOW, from a sign-in flow (SOC 2 Phase 1, decision 4, 2026-10-13).
 *
 * A brand-new account can reach the app without ticking the terms box: Google pressed on the log-in card when the
 * pre-check came back empty (src/lib/identity/googleSignInCheck.ts), and the Google redirect landing
 * (/auth/callback), which has no box at all. Such a flow asks here, BEFORE its trial starts or anything is set up
 * for the account; TermsUpdateHost (always mounted, src/components/legal/TermsUpdateHost.tsx) shows the one-time
 * terms dialog and answers. One dialog serves both this and the existing accounts with no record.
 *
 * Firebase-free and tiny, because the sign-in pages' chunks carry it.
 */
import type { TermsConsentSignInMethod } from './termsConsentVocabulary';

export type TermsDialogAnswer = 'accepted' | 'signed-out' | 'no-host';

export interface TermsDialogRequest {
  uid: string;
  signInMethod: TermsConsentSignInMethod;
}

export type TermsDialogServer = (request: TermsDialogRequest, answer: (a: 'accepted' | 'signed-out') => void) => void;

/**
 * How long a request waits for a host to pick it up. Past it the flow goes on without the dialog — the host asks on
 * the next screen anyway, since the account has no record — so a host that failed to load never wedges sign-in.
 * Once a host has the request, the person takes as long as they like.
 */
export const TERMS_DIALOG_HOST_WAIT_MS = 10_000;

let server: TermsDialogServer | null = null;
let waiting: { deliver: () => void; finish: (a: TermsDialogAnswer) => void } | null = null;

/** Show the terms dialog for this new account and wait for the person's answer. */
export function requestTermsAcceptance(request: TermsDialogRequest): Promise<TermsDialogAnswer> {
  return new Promise<TermsDialogAnswer>((resolve) => {
    let done = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const finish = (a: TermsDialogAnswer) => {
      if (done) return;
      done = true;
      if (timer) clearTimeout(timer);
      if (waiting?.finish === finish) waiting = null;
      resolve(a);
    };
    const deliver = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      if (server) server(request, finish);
      else finish('no-host');
    };
    // One sign-in at a time: an earlier request still waiting for a host goes on without the dialog.
    waiting?.finish('no-host');
    if (server) {
      deliver();
    } else {
      timer = setTimeout(() => finish('no-host'), TERMS_DIALOG_HOST_WAIT_MS);
      waiting = { deliver, finish };
    }
  });
}

/** The host serves requests while it is mounted; a request made before it mounted is delivered now. */
export function serveTermsDialogRequests(next: TermsDialogServer): () => void {
  server = next;
  if (waiting) {
    const w = waiting;
    waiting = null;
    w.deliver();
  }
  return () => {
    if (server === next) server = null;
  };
}

/** Tests only. */
export function __resetTermsDialogRequestsForTests(): void {
  server = null;
  waiting = null;
}
